import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../rootReducer';
import { Ifilter, FilterConnectors, FilterOptions, FilterNames, FilterOperators } from '../../components/common/filters/interfaces';
import { fetchDropdownOptions } from './sideMenuFilters.thunks';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

import { AccessTypes } from '../../components/common/domain/accountAccessType';
import { actions as filterDashboardActions } from './dashboardFilters';

export interface FilterState {
  visible: boolean;
  filtersInput: Ifilter[];
  appliedFilters: Ifilter[];
  filterOptions: FilterOptions[];
  filterAppliedState: boolean;
  // use for Features.PlgExternal3
  accountTypeFilterId: string;
  // end of use for Features.PlgExternal3
}

interface ChangeTypePayload {
  id?: string;
  field: string;
}

const initialState: FilterState = {
  visible: false,
  filtersInput: [],
  appliedFilters: [],
  filterOptions: [],
  filterAppliedState: false,
  accountTypeFilterId: ''
};

const isPlgExternal3Env = isFeatureEnabled(Features.PlgExternal3);

const getAccountFilterState = (state: RootState): FilterState => state.filters.menu;
const isVisible = (state: RootState): boolean => getAccountFilterState(state).visible;
const getFiltersInput = (state: RootState): Ifilter[] => getAccountFilterState(state).filtersInput;
const getAppliedFilters = (state: RootState): Ifilter[] => getAccountFilterState(state).appliedFilters;
const getFilterOptions = (state: RootState): FilterOptions[] => getAccountFilterState(state).filterOptions;
const getFilterAppliedState = (state: RootState): boolean => getAccountFilterState(state).filterAppliedState;
const getAccountTypeFilterId = (state: RootState): string | undefined => getAccountFilterState(state).accountTypeFilterId;

export const selectors = {
  isVisible,
  getFiltersInput,
  getAppliedFilters,
  getFilterOptions,
  getFilterAppliedState,
  getAccountTypeFilterId
};

const reducers = {
  setFiltersMenuVisibility: (state: FilterState, action: PayloadAction<boolean>) => {
    state.visible = action.payload;
    state.filtersInput = state.appliedFilters.map(filter => ({ ...filter, validateInputs: false }));
  },
  deleteFilter: (state: FilterState, action: PayloadAction<string>) => {
    state.filtersInput = state.filtersInput.filter(e => e.id !== action.payload);
    if (state.filtersInput.length > 0) {
      state.filtersInput[0].connector = FilterConnectors.none;
    }
    if(state.accountTypeFilterId === action.payload) {
      state.accountTypeFilterId = '';
    }
  },
  createFilter: (state: FilterState, action: PayloadAction<string>) => {
    const id = nanoid();
    const newFilter: Ifilter = {
      id,
      field: action.payload,
      operator: '',
      operands: [],
      connector: FilterConnectors.and,
      validateInputs: false
    };
    state.filtersInput = [...state.filtersInput, newFilter];
    if (state.filtersInput.length > 0) {
      state.filtersInput[0].connector = FilterConnectors.none;
    }
    if(isPlgExternal3Env && !state.accountTypeFilterId) {
      state.accountTypeFilterId = id;
    }
  },
  changeFilterType: (state: FilterState, action: PayloadAction<ChangeTypePayload>) => {
    state.filtersInput = state.filtersInput.map((filter) => (filter.id === action.payload.id) ?
      { ...filter, field: action.payload.field, operator: '', operands: [] } :
      filter
    );
  },
  editFilter: (state: FilterState, action: PayloadAction<Ifilter>) => {
    state.filtersInput = state.filtersInput.map((filter) => (filter.id === action.payload.id) ?
      ({ ...action.payload }) :
      filter
    );
  },
  validateFilters: (state: FilterState, action: PayloadAction<boolean>) => {
    state.filtersInput = state.filtersInput.map(filter => ({ ...filter, validateInputs: action.payload }));
  },
  resetFilters: (state: FilterState) => {
    state.visible = false;
    state.filtersInput = [];
    state.appliedFilters = [];
    if(isPlgExternal3Env) {
      state.accountTypeFilterId = '';
    }
  },
  setAppliedFilters: (state: FilterState, action: PayloadAction<Ifilter[]>) => {
    state.appliedFilters = [...action.payload];
  },
  setFilterAppliedState: (state: FilterState, action: PayloadAction<boolean>) => {
    state.filterAppliedState = action.payload;
  }
};

interface Entry {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}
interface FetchDropdownsPayload {
  accounts?: Entry[];
  managers?: Entry[];
  customerSuccessManagers?: Entry[];
  teams?: Entry[];
}

const fullName = (entry: Entry) => {
  return entry.name ||
    `${(entry.firstName) ? entry.firstName : ''}${(entry.lastName) ? ` ${entry.lastName}` : ''}`;
};

const sorter = (a: string, b: string): number => {
  return a.toLocaleLowerCase().localeCompare(b);
};

export const extraReducers = {
  [fetchDropdownOptions.pending.type]: (state: FilterState): void => {
    state.filterOptions = [];
  },
  [fetchDropdownOptions.fulfilled.type]: (state: FilterState, action: PayloadAction<FetchDropdownsPayload>): void => {
    state.filterOptions = [];
    if (action.payload.accounts) {
      const options: FilterOptions = { fieldName: FilterNames.AccountName, operators: [] };
      action.payload.accounts.sort((a, b) => sorter(fullName(a), fullName(b))).forEach((account: Entry) => {
        options.operators.push({ value: account.id, label: fullName(account) });
      });
      state.filterOptions.push(options);
    }

    if (action.payload.managers) {
      const options: FilterOptions = { fieldName: FilterNames.AccountManager, operators: [] };
      action.payload.managers.sort((a, b) => sorter(fullName(a), fullName(b))).forEach((manager: Entry) => {
        options.operators.push({ id: manager.id, value: manager.id, label: fullName(manager) });
      });
      state.filterOptions.push(options);
    }

    if (action.payload.customerSuccessManagers) {
      const options: FilterOptions = { fieldName: FilterNames.CustomerSuccessManager, operators: [] };
      action.payload.customerSuccessManagers.sort((a, b) => sorter(fullName(a), fullName(b))).forEach((csm: Entry) => {
        options.operators.push({ id: csm.id, value: csm.id, label: fullName(csm) });
      });
      state.filterOptions.push(options);
    }

    if (action.payload.teams) {
      const options: FilterOptions = { fieldName: FilterNames.TeamName, operators: [] };
      action.payload.teams.sort((a, b) => sorter(fullName(a), fullName(b))).forEach((team: Entry) => {
        options.operators.push({ value: team.id, label: fullName(team) });
      });
      state.filterOptions.push(options);
    }
  },
  [filterDashboardActions.setAccountType.type]: (state: FilterState, action: PayloadAction<AccessTypes | null>): void => {
    if (!isFeatureEnabled(Features.PlgExternal3)) {
      return;
    }
    const accountType = action.payload;
    if (accountType) {
      state.appliedFilters = addAccountTypeFilter(state.appliedFilters, accountType);
    }
  }
};

export const addAccountTypeFilter = (filters: Ifilter[], accountType: AccessTypes | null): Ifilter[] => {
  if (!accountType || !isFeatureEnabled(Features.PlgExternal3)) {
    return filters;
  }
  const updatedFilters = [...filters];
  const position = updatedFilters.findIndex((filter) => filter.field === 'accessType');
  const accessTypeFilter = {
    field: 'accessType',
    operands: [accountType],
    operator: FilterOperators.isAnyOf,
    validateInputs: false
  };
  if (position >= 0 ) {
    updatedFilters[position] = accessTypeFilter;
  } else {
    updatedFilters.push(accessTypeFilter);
  }
  return updatedFilters;
};

const sideMenuFilterSlice = createSlice({
  name: 'sideMenuFilters',
  initialState,
  reducers,
  extraReducers
});

export const actions = { ...sideMenuFilterSlice.actions };
export default sideMenuFilterSlice.reducer;
